import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = '#FFC107';
const GREY = '#3a4252';

// Single SVG star with optional fractional fill (0..1).
// We use a clipPath on a fixed gradient to render half-stars based on decimal averages.
function Star({ fill = 0, size = 26, glow = false, className = '' }) {
  const id = useRef(`star-grad-${Math.random().toString(36).slice(2, 9)}`).current;
  const pct = Math.max(0, Math.min(1, fill));
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={glow ? { filter: `drop-shadow(0 0 6px ${GOLD}99)` } : undefined}
    >
      <defs>
        <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
          <stop offset={`${pct * 100}%`} stopColor={GOLD} />
          <stop offset={`${pct * 100}%`} stopColor={GREY} />
        </linearGradient>
      </defs>
      <path
        d="M12 2.5l2.95 6.45 7.05.75-5.3 4.85L18.2 21.5 12 17.85 5.8 21.5l1.5-6.95L2 9.7l7.05-.75L12 2.5z"
        fill={`url(#${id})`}
        stroke={fill > 0 ? GOLD : GREY}
        strokeWidth="0.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function RatingBar({ prominent = false, onRated }) {
  const { user, token } = useAuth();
  const [average, setAverage] = useState(4.9);
  const [count, setCount] = useState(7);
  const [myScore, setMyScore] = useState(null);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [thanksUntil, setThanksUntil] = useState(0);

  // Fetch on mount + when auth changes
  useEffect(() => {
    let cancelled = false;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API}/ratings`, { headers })
      .then((res) => {
        if (cancelled) return;
        setAverage(res.data.average);
        setCount(res.data.count);
        setMyScore(res.data.my_score);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleClick = async (score) => {
    if (!user || !token) {
      toast.error('Please sign in to rate this tool.');
      return;
    }
    if (submitting) return;

    // Optimistic update — recompute display avg/count locally
    const prev = { average, count, myScore };
    const replacing = myScore != null;
    // Backend formula: (34 + sumReal) / (7 + countReal)
    // current: average = (34 + sumReal) / count
    //   sumReal = average * count - 34
    const sumReal = average * count - 34;
    const countReal = count - 7;
    const newSumReal = replacing ? sumReal - prev.myScore + score : sumReal + score;
    const newCountReal = replacing ? countReal : countReal + 1;
    const newDisplayCount = 7 + newCountReal;
    const newDisplayAvg = (34 + newSumReal) / newDisplayCount;
    setAverage(Math.round(newDisplayAvg * 100) / 100);
    setCount(newDisplayCount);
    setMyScore(score);
    setSubmitting(true);

    try {
      const res = await axios.post(
        `${API}/ratings`,
        { score },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAverage(res.data.average);
      setCount(res.data.count);
      setMyScore(res.data.my_score);
      setThanksUntil(Date.now() + 2200);
      // After the thank-you flash, let the parent relocate the bar (top → bottom).
      if (onRated) setTimeout(() => onRated(score), 1800);
    } catch (err) {
      // Revert
      setAverage(prev.average);
      setCount(prev.count);
      setMyScore(prev.myScore);
      toast.error('Could not save your rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Display fill for each star: prefer hover (if logged in) > my_score (sticky) > average
  const displayedScore =
    hover > 0 ? hover : myScore != null ? myScore : average;
  const isInteractive = !!user && !!token;
  const showThanks = thanksUntil > Date.now();

  // Re-render when "Thanks" expires
  useEffect(() => {
    if (!showThanks) return;
    const t = setTimeout(() => setThanksUntil(0), thanksUntil - Date.now() + 50);
    return () => clearTimeout(t);
  }, [showThanks, thanksUntil]);

  const fillFor = (i) => {
    // For integer hover/myScore, render full stars
    if (hover > 0 || myScore != null) {
      const baseline = hover > 0 ? hover : myScore;
      return i < baseline ? 1 : 0;
    }
    // For decimal average, support partial fill
    const v = displayedScore - i;
    if (v >= 1) return 1;
    if (v <= 0) return 0;
    return v;
  };

  const starsRow = (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHover(0)}
      data-testid="rating-stars"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => handleClick(i + 1)}
          onMouseEnter={() => isInteractive && setHover(i + 1)}
          className={`p-1 rounded transition-transform duration-150 ${
            isInteractive ? 'hover:scale-125 cursor-pointer' : 'cursor-default'
          } ${submitting ? 'opacity-70' : ''}`}
          aria-label={`Rate ${i + 1} star${i === 0 ? '' : 's'}`}
          data-testid={`rating-star-${i + 1}`}
          disabled={submitting}
        >
          <Star
            fill={fillFor(i)}
            size={prominent ? 34 : 28}
            glow={isInteractive && hover === i + 1}
          />
        </button>
      ))}
    </div>
  );

  const meta = showThanks ? (
    <span
      className="text-sm sm:text-base font-semibold text-[#FFC107]"
      data-testid="rating-thanks"
    >
      Thanks for your feedback!
    </span>
  ) : (
    <>
      <span
        className="text-sm sm:text-base font-bold text-white tabular-nums"
        data-testid="rating-score"
      >
        {average.toFixed(1)} / 5
      </span>
      <span
        className="text-xs sm:text-sm text-gray-400"
        data-testid="rating-count"
      >
        {count.toLocaleString()} ratings from verified creators
      </span>
    </>
  );

  if (prominent) {
    return (
      <div className="w-full flex justify-center mt-2 mb-10 px-4" data-testid="rating-bar-prominent">
        <div className="w-full max-w-xl rounded-2xl border border-[#FFC107]/30 bg-[#161C2E]/80 backdrop-blur-md px-6 py-5 shadow-lg text-center">
          <p className="text-base sm:text-lg font-semibold text-white mb-1">
            Enjoying Visual Atmos?
          </p>
          <p className="text-xs sm:text-sm text-slate-400 mb-4">
            Tap a star to rate your experience — it only takes a second and helps us a lot.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {starsRow}
            {meta}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full flex justify-center mt-16 mb-10 px-4"
      data-testid="rating-bar"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
        <span className="text-sm sm:text-base text-gray-300 font-medium" data-testid="rating-label">
          Rate this tool
        </span>
        {starsRow}
        {meta}
      </div>
    </div>
  );
}
